/**
 * Warm the destination product app (pro / certification) while the user is
 * still on account-web — especially during the Google OAuth wait and the
 * callback code-exchange window. Locally this helps Vite transform modules
 * into its cache before the hard navigate; in prod it primes DNS/TLS + HTML.
 */

import { getProAppOrigin } from "./app-origins";

const prefetched = new Set<string>();

function ensureLink(rel: string, href: string, attrs?: Record<string, string>): void {
  if (typeof document === "undefined") return;
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      link.setAttribute(k, v);
    }
  }
  document.head.appendChild(link);
}

/**
 * Prefetch a product-app URL (document + origin hints). Safe to call repeatedly.
 */
export function prefetchProductAppUrl(targetUrl: string): void {
  if (typeof window === "undefined" || !targetUrl) return;

  let origin: string;
  let href: string;
  try {
    const parsed = new URL(targetUrl, window.location.origin);
    // Only warm known http(s) product destinations.
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return;
    origin = parsed.origin;
    href = parsed.toString();
  } catch {
    return;
  }

  if (prefetched.has(href)) return;
  prefetched.add(href);

  ensureLink("dns-prefetch", origin);
  ensureLink("preconnect", origin, { crossorigin: "" });
  // Prefer speculative document load where supported.
  ensureLink("prefetch", href, { as: "document" });

  // Kick the destination origin so local Vite starts serving/transforming.
  // no-cors avoids CORS noise; opaque response is fine — we only want network heat.
  try {
    void fetch(href, {
      mode: "no-cors",
      credentials: "omit",
      cache: "force-cache",
    } as RequestInit).catch(() => {
      /* ignore */
    });
  } catch {
    /* ignore */
  }
}

/** Prefetch the default pro-web origin (home). */
export function prefetchProApp(): void {
  prefetchProductAppUrl(`${getProAppOrigin()}/`);
}
